<template>
  <div class="scrollable-div display-scrollbar" ref="scrollableDiv">
    <table class="full-width-table">
      <tbody>
        <tr class="table-header">
          <th style="width: 10%">Name</th>
          <th style="width: 14%">Email Address</th>
          <th style="width: 12%">Roles</th>
          <th style="width: 14%">Modules</th>
          <th style="width: 12%">Admin Privileges</th>
          <th style="width: 12%">Production Schedule</th>
          <th style="width: 8%">Out of Office</th>
          <th style="min-width: 2rem; width: 3%"></th>
        </tr>
        <tr v-if="!loading" v-for="(user, index) in allActiveUsers" class="user-row">
          <td class="link" @click="showEditUser = user.USERID">
            <span>{{ user.FIRSTNAME }} {{ user.LASTNAME }}</span>
          </td>
          <td>
            <span>{{ user.EMAIL }}</span>
          </td>
          <td>
            <span>{{ formatRoles(user) }}</span>
          </td>
          <td>
            <span>{{ formatModules(user) }}</span>
          </td>
          <td>
            <span>{{ formatPermissions(user) }}</span>
          </td>
          <td>
            <span>{{ formatProdSchedule(user) }}</span>
          </td>
          <td>
            <span>{{ user.OOO ? 'Yes' : 'No' }}</span>
          </td>
          <td class="context-container">
            <div class="hover-icon" v-click-outside="closeContextMenu" @click.stop="showContextMenu = index">
              <img id="noHover" src="@/assets/three-dots-menu.svg" />
              <img id="hover" src="@/assets/three-dots-menu-hover.svg" />
              <div class="dropdown hover-style no-images" v-if="showContextMenu == index">
                <div
                  class="context-menu-item"
                  @click.stop="
                    closeContextMenu();
                    showEditUser = user.USERID;
                  ">
                  <span>Information & Settings</span>
                </div>
                <div
                  class="context-menu-item"
                  @click.stop="
                    resetMFA(user.USERID);
                    closeContextMenu();
                  ">
                  <span>Reset MFA</span>
                </div>
                <div
                  class="context-menu-item"
                  @click.stop="
                    resendWelcomeEmail(user.USERID);
                    closeContextMenu();
                  ">
                  <span>Resend Welcome Email</span>
                </div>
                <div
                  class="context-menu-item"
                  @click.stop="
                    closeContextMenu();
                    showDeleteConfirmation = user.USERID;
                  ">
                  <span>Delete</span>
                </div>
              </div>
            </div>
          </td>
        </tr>
        <tr v-if="loading" class="loading-row">
          <td colspan="13"><img src="../../assets/spinner.svg" /></td>
        </tr>
      </tbody>
    </table>
  </div>
  <Confirmation
    v-if="showDeleteConfirmation"
    :serious="true"
    header="Delete"
    message="Are you sure you want to delete this user?"
    confirmText="Delete"
    @confirm="deleteUser()"
    cancelText="Don't Delete"
    @cancel="showDeleteConfirmation = null" />
  <EditUser v-if="showEditUser" :userid="showEditUser" @closePopup="showEditUser = null" />
</template>

<script>
import useUserMutate from '@/composables/useUserMutate';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import Confirmation from '@/components/Confirmation.vue';
import useAlerts from '@/composables/useAlerts';
import useUsersQuery from '@/composables/useUsersQuery';
import EditUser from '@/components/EditUser.vue';
import { resetMFA } from '@/composables/useAuthMutate';
import { resendWelcomeEmail } from '@/composables/useAuthMutate';

export default {
  name: 'Users',
  emits: ['isDirty', 'isSaving'],
  components: {
    Confirmation,
    EditUser,
  },
  setup() {
    const { decryptSymbols, encryptSymbols } = useDecryptSymbols();
    const { allActiveUsers, getUsers } = useUsersQuery();
    const { deleteUser } = useUserMutate();
    const { addAlert } = useAlerts();
    const { loading } = getUsers();

    return {
      allActiveUsers,
      decryptSymbols,
      encryptSymbols,
      deleteUser,
      addAlert,
      getUsers,
      loading,
      resetMFA,
      resendWelcomeEmail,
    };
  },
  data() {
    return {
      showDeleteConfirmation: null,
      showContextMenu: null,
      showEditUser: false,
    };
  },
  methods: {
    closeContextMenu() {
      this.showContextMenu = null;
    },
    formatRoles(user) {
      const roles = [];
      if (user.INSIDESALES) roles.push('Inside Sales');
      if (user.OUTSIDESALES) roles.push('Outside Sales');
      return roles.join(', ');
    },
    formatModules(user) {
      const modules = [];
      if (user.SHOWLEADS) modules.push('Leads');
      if (user.SHOWQUOTES) modules.push('Quotes');
      if (user.SHOWCONTACTS) modules.push('Contacts');
      if (user.SHOWGENERALREPORTS) modules.push('General Reports');
      if (user.SHOWIMPACTREPORT) modules.push('Sales Impact Report');
      return modules.join(', ');
    },
    formatPermissions(user) {
      const permissions = [];
      if (user.LEADSADMIN) permissions.push('Leads');
      if (user.QUOTESADMIN) permissions.push('Quotes');
      if (user.CONTACTSADMIN) permissions.push('Contacts');
      if (user.ADMIN) permissions.push('Users');
      return permissions.join(', ');
    },
    formatProdSchedule(user) {
      const labels = {
        admin: 'Admin',
        scheduler: 'Scheduler',
        production: 'Production',
        lookup: 'Lookup',
      };
      return labels[user.PRODSCHEDULELEVEL] || '';
    },
    add() {
      this.showEditUser = 'new';
    },
    deleteUser() {
      this.deleteUser(this.showDeleteConfirmation).then((success) => {
        if (success) {
          this.getUsers();
          this.addAlert('User deleted', 'low', 3);
        }
      });
      this.showDeleteConfirmation = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.link {
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.full-width-table .user-row {
  td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

td.context-container {
  .hover-icon {
    position: relative;
    overflow: visible;

    .dropdown {
      right: 0;
      top: 105%;
    }
  }
}
</style>
